import React, { useState, useEffect } from 'react';

/**
 * SlamFitGauge -- Reusable circular SVG gauge for SLAM Fit Score.
 *
 * Props:
 *   score       {number}  0-100
 *   size        {number}  outer px (default 80)
 *   strokeWidth {number}  ring thickness (default 6)
 *   label       {string}  optional caption below the score
 */
export default function SlamFitGauge({ score = 0, size = 80, strokeWidth = 6, label }) {
  const [animatedScore, setAnimatedScore] = useState(0);

  useEffect(() => {
    // Animate from 0 to target score on mount / change
    const timeout = setTimeout(() => setAnimatedScore(score), 50);
    return () => clearTimeout(timeout);
  }, [score]);

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.min(Math.max(animatedScore, 0), 100);
  const offset = circumference - (progress / 100) * circumference;

  // Color thresholds: red < 40, amber 40-69, green >= 70
  const color =
    progress >= 70 ? '#00CC66' :
    progress >= 40 ? '#F59E0B' :
    '#FF4444';

  return (
    <div className="relative flex flex-col items-center">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        {/* Background ring */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#1F1F1F"
          strokeWidth={strokeWidth}
        />
        {/* Foreground arc */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{ transition: 'stroke-dashoffset 0.8s ease-out, stroke 0.4s ease' }}
        />
      </svg>

      {/* Centered score */}
      <div
        className="absolute flex flex-col items-center justify-center"
        style={{ width: size, height: size }}
      >
        <span
          className="font-semibold"
          style={{
            fontSize: size * 0.28,
            color,
            lineHeight: 1,
          }}
        >
          {Math.round(progress)}
        </span>
      </div>

      {label && (
        <span className="mt-1 text-[10px] tracking-wider uppercase text-zinc-500">
          {label}
        </span>
      )}
    </div>
  );
}
